/**
 * EditableExpiry — inline editor for an estimate's expires_at column.
 *
 * Used wherever an operator needs to bump or shorten a proposal's
 * validity window: the Estimator page header (post-save), the Quick
 * View modal on Proposals, and inside the Share Proposal modal.
 *
 * Writes straight to estimates.expires_at via the supabase client.
 * Anchors the new value at end-of-day in the operator's local timezone
 * so a proposal "valid until 19 May" is still live at 23:59 on that day.
 */

import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';

interface EditableExpiryProps {
  estimateId: string;
  expiresAt: string | null | undefined;
  onChange?: (newIso: string) => void;
  /** Small visual variant used inline next to a reference code. */
  compact?: boolean;
}

export default function EditableExpiry({ estimateId, expiresAt, onChange, compact = false }: EditableExpiryProps) {
  const [updating, setUpdating] = useState(false);
  const [local, setLocal] = useState<string | null | undefined>(expiresAt);

  const current = local ?? expiresAt;
  const dateValue = current ? new Date(current).toISOString().slice(0, 10) : '';
  const todayValue = new Date().toISOString().slice(0, 10);

  async function handleChange(newDateYmd: string) {
    if (!estimateId || !newDateYmd) return;
    const localEod = new Date(`${newDateYmd}T23:59:59`);
    const iso = localEod.toISOString();
    setUpdating(true);
    const { error } = await supabase
      .from('estimates')
      .update({ expires_at: iso })
      .eq('id', estimateId);

    // Pushing the date forward has to lift the expired stamp too, otherwise
    // the proposal still reads as Lost on the board and the customer still
    // sees the Expired ribbon. It goes back to where it was before it lapsed.
    if (!error && localEod > new Date()) {
      const { data: row } = await supabase
        .from('estimates')
        .select('status, viewed_at')
        .eq('id', estimateId)
        .single();

      if (row?.status === 'expired') {
        await supabase
          .from('estimates')
          .update({ status: row.viewed_at ? 'viewed' : 'sent' })
          .eq('id', estimateId);
      }
    }

    setUpdating(false);
    if (error) {
      alert(`Failed to update expiry: ${error.message}`);
      return;
    }
    setLocal(iso);
    onChange?.(iso);
  }

  const fontSize = compact ? '0.8125rem' : '0.875rem';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize, flexWrap: 'wrap' }}>
      <span className="text-muted">Valid until</span>
      <input
        type="date"
        className="form-input"
        style={{ padding: '0.2rem 0.45rem', fontSize, maxWidth: '160px' }}
        value={dateValue}
        min={todayValue}
        onChange={(e) => handleChange(e.target.value)}
        disabled={updating}
      />
      {updating && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)' }}>Saving…</span>}
    </span>
  );
}
